/**
 * Stammdaten-Tab helpers — reine Formatierungs-/Build-Logik für den
 * Stammdaten-Tab (Tab 1) im Fahrzeug-Detail-Drawer.
 *
 * Alle Funktionen sind pure und seiteneffektfrei, damit sie in Unit-Tests
 * ohne React- oder DB-Setup geprüft werden können. Die Tab-Komponente
 * selbst rendert nur die von `buildStammdatenRows` zurückgegebenen Zeilen
 * und behandelt km_stand (editierbar) separat.
 */

export interface FahrzeugStammdaten {
  kennzeichen: string | null;
  fin: string | null;
  hsn: string | null;
  tsn: string | null;
  erstzulassung: string | null;
  kraftstoffart: string | null;
  farbe: string | null;
  kw: number | null;
  hubraum_ccm: number | null;
  batteriekapazitaet_kwh: number | null;
  wltp_reichweite_km: number | null;
  km_stand: number | null;
  km_stand_aktualisiert_am: string | null;
}

export interface StammdatenRow {
  label: string;
  value: string;
}

export interface StammdatenCi {
  accent: string;
  fontFamily: string;
}

const EM_DASH = '—';

const KRAFTSTOFF_LABELS: Record<string, string> = {
  benzin: 'Benzin',
  diesel: 'Diesel',
  elektro: 'Elektro',
  hybrid: 'Hybrid',
  plugin_hybrid: 'Plug-in-Hybrid',
};

const LCS_FONT = 'Montserrat, system-ui, sans-serif';
const SV_FONT = 'Inter, system-ui, sans-serif';

const LCS_CI: StammdatenCi = { accent: '#0056B3', fontFamily: LCS_FONT };
const SV_CI: StammdatenCi = { accent: '#06B6D4', fontFamily: SV_FONT };

const SV_UNITS = new Set(['gutachten']);
const LCS_UNITS = new Set(['autoglas', 'mietwagen', 'werkstatt']);

function hasText(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

function formatDateDE(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export function formatKw(value: number | null | undefined): string {
  if (!hasNumber(value)) return EM_DASH;
  const ps = Math.round(value * 1.35962);
  return `${value} kW (${ps} PS)`;
}

export function formatCcm(value: number | null | undefined): string {
  if (!hasNumber(value)) return EM_DASH;
  return `${value.toLocaleString('de-DE')} ccm`;
}

export function formatKm(value: number | null | undefined): string {
  if (!hasNumber(value)) return EM_DASH;
  return `${value.toLocaleString('de-DE')} km`;
}

export function formatKwh(value: number | null | undefined): string {
  if (!hasNumber(value)) return EM_DASH;
  return `${value.toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} kWh`;
}

export function formatReichweiteKm(value: number | null | undefined): string {
  if (!hasNumber(value)) return EM_DASH;
  return `${value.toLocaleString('de-DE')} km (WLTP)`;
}

export function formatKraftstoff(value: string | null | undefined): string {
  if (!hasText(value)) return EM_DASH;
  return KRAFTSTOFF_LABELS[value] ?? value;
}

export function formatKmAktualisiert(iso: string | null | undefined): string {
  const date = formatDateDE(iso);
  if (!date) return 'noch nie aktualisiert';
  return `zuletzt am ${date}`;
}

function formatHsnTsn(hsn: string | null, tsn: string | null): string | null {
  const h = hasText(hsn) ? hsn.trim() : null;
  const t = hasText(tsn) ? tsn.trim() : null;
  if (h && t) return `${h} / ${t}`;
  if (h) return h;
  if (t) return t;
  return null;
}

/**
 * Baut die anzeigbaren Zeilen des Stammdaten-Tabs. km_stand wird bewusst
 * ausgelassen, weil er als editierbare Inline-Komponente gesondert gerendert
 * wird. Leere Felder werden übersprungen — dadurch zeigt der Tab für ein
 * frisch angelegtes Fahrzeug nur die tatsächlich gepflegten Daten.
 */
export function buildStammdatenRows(fz: FahrzeugStammdaten): StammdatenRow[] {
  const rows: StammdatenRow[] = [];
  if (hasText(fz.kennzeichen)) {
    rows.push({ label: 'Kennzeichen', value: fz.kennzeichen.trim() });
  }
  if (hasText(fz.fin)) {
    rows.push({ label: 'FIN', value: fz.fin.trim() });
  }
  const hsnTsn = formatHsnTsn(fz.hsn, fz.tsn);
  if (hsnTsn) {
    rows.push({ label: 'HSN/TSN', value: hsnTsn });
  }
  const ez = formatDateDE(fz.erstzulassung);
  if (ez) {
    rows.push({ label: 'Erstzulassung', value: ez });
  }
  if (hasText(fz.kraftstoffart)) {
    rows.push({ label: 'Kraftstoffart', value: formatKraftstoff(fz.kraftstoffart) });
  }
  if (hasText(fz.farbe)) {
    rows.push({ label: 'Farbe', value: fz.farbe.trim() });
  }
  if (hasNumber(fz.kw)) {
    rows.push({ label: 'Leistung', value: formatKw(fz.kw) });
  }
  if (hasNumber(fz.hubraum_ccm)) {
    rows.push({ label: 'Hubraum', value: formatCcm(fz.hubraum_ccm) });
  }
  if (hasNumber(fz.batteriekapazitaet_kwh)) {
    rows.push({ label: 'Batteriekapazität', value: formatKwh(fz.batteriekapazitaet_kwh) });
  }
  if (hasNumber(fz.wltp_reichweite_km)) {
    rows.push({ label: 'WLTP-Reichweite', value: formatReichweiteKm(fz.wltp_reichweite_km) });
  }
  return rows;
}

/**
 * CI-Akzent pro business_unit. Gutachten → SV (türkis + Inter); alles andere
 * inkl. gemischter LCS-Präfixe wie `lcs_mietwagen` → LCS (blau + Montserrat).
 * Fallback: LCS, da der Fuhrpark überwiegend LCS gehört.
 */
export function resolveStammdatenCi(businessUnit: string | null | undefined): StammdatenCi {
  if (!hasText(businessUnit)) return LCS_CI;
  const key = businessUnit.toLowerCase();
  if (SV_UNITS.has(key) || key.startsWith('sv_')) return SV_CI;
  if (LCS_UNITS.has(key) || key.startsWith('lcs_')) return LCS_CI;
  return LCS_CI;
}
